/* ============================================================
   Analytics loading.

   gtag (183 KB) and Clarity (28 KB) used to be injected into <head> by their
   Gatsby plugins, so they competed for bandwidth with the page's own JS and
   cost ~560ms of main-thread time before the page was interactive. Both are
   now bootstrapped on idle instead.

   `window.gtag` and `window.dataLayer` are defined immediately (no network),
   so `gtag('event', ...)` calls fired by components before the script lands
   are queued in dataLayer and replayed by GA once it loads.
   ============================================================ */

export const GA_MEASUREMENT_ID = 'G-CT2FK911BW'
export const CLARITY_PROJECT_ID = 'l4v5jdz0jq'

type AnyWindow = Window & { [key: string]: any }

const isBrowser = () => typeof window !== 'undefined'

/** Analytics is production-only — dev traffic would pollute both dashboards. */
const enabled = () => isBrowser() && process.env.NODE_ENV === 'production'

/** Define the dataLayer queue synchronously so no early event is dropped. */
export function primeAnalytics(): void {
  if (!enabled()) return
  const w = window as AnyWindow
  if (w.gtag) return

  w.dataLayer = w.dataLayer || []
  w.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer.push(arguments)
  }
  w.gtag('js', new Date())
  w.gtag('config', GA_MEASUREMENT_ID)
}

let loaded = false

function injectScript(src: string): void {
  const script = document.createElement('script')
  script.src = src
  script.async = true
  document.head.appendChild(script)
}

/** Fetch the actual vendor scripts. Safe to call more than once. */
export function loadAnalytics(): void {
  if (!enabled() || loaded) return
  loaded = true

  primeAnalytics()
  injectScript(`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`)

  const w = window as AnyWindow
  w.clarity =
    w.clarity ||
    function clarity() {
      // eslint-disable-next-line prefer-rest-params
      ;(w.clarity.q = w.clarity.q || []).push(arguments)
    }
  injectScript(`https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`)
}

/** GA4 page_view for client-side route changes (the initial view ships with `config`). */
export function trackPageView(path: string): void {
  if (!enabled()) return
  const w = window as AnyWindow
  if (typeof w.gtag !== 'function') return
  w.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title
  })
}

/**
 * Fire a GA4 event. Safe everywhere: a no-op during SSR, outside production, or
 * when gtag is missing. Before the vendor script lands the call is queued in
 * dataLayer (see primeAnalytics) and replayed once GA loads.
 */
export function trackEvent(name: string, params: Record<string, unknown> = {}): void {
  if (!enabled()) return
  try {
    const w = window as AnyWindow
    if (typeof w.gtag !== 'function') return
    w.gtag('event', name, params)
  } catch {
    /* analytics must never break the page */
  }
}

let ctaTrackingInstalled = false

/**
 * One delegated listener for every CTA tagged `data-cta="<placement>"`:
 * sends `cta_click { cta, page }`. Capture phase, so it still fires when a
 * handler stops propagation or navigates away in the same tick.
 */
export function installCtaClickTracking(): void {
  if (!enabled() || ctaTrackingInstalled) return
  ctaTrackingInstalled = true

  document.addEventListener(
    'click',
    (e) => {
      const target = e.target as Element | null
      const el = target && typeof target.closest === 'function' ? (target.closest('[data-cta]') as HTMLElement | null) : null
      const cta = el?.dataset.cta
      if (!cta) return
      trackEvent('cta_click', { cta, page: window.location.pathname })
    },
    { capture: true, passive: true }
  )
}
